/**
 * useChatSocket — React hook wrapping the HustleChat WebSocket bridge.
 *
 * Usage:
 *   const sock = useChatSocket({
 *     enabled: true,
 *     convIds: [convId],         // auto-subscribed on connect
 *     onNewMessage: (d) => …,    // d = {conv_id, message_id, sender_id, created_at, ephemeral}
 *     onPresence:   (d) => …,    // d = {who, online}
 *     onTyping:     (d) => …,    // d = {conv_id, member_id, on}
 *   });
 *   sock.sendTyping(convId, true);     // fires {t:"typing", conv_id, on:true}
 *   sock.connected                      // bool
 *
 * Gracefully degrades — if WS won't connect (older proxy, dev preview
 * quirk, etc.) the caller's existing REST polling fallback still works.
 */
import { useEffect, useRef, useState, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function wsUrl(token) {
  // Convert https://… → wss://…  (or http→ws for localhost)
  const base = API.replace(/^http/, "ws");
  return `${base}/api/hustlechat/ws?token=${encodeURIComponent(token)}`;
}

export function useChatSocket({ enabled = true, convIds = [],
                                  onNewMessage, onPresence, onTyping } = {}) {
  const wsRef            = useRef(null);
  const reconnectRef     = useRef(0);
  const [connected, setConnected] = useState(false);

  const sendTyping = useCallback((conv_id, on) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    ws.send(JSON.stringify({ t: "typing", conv_id, on: !!on }));
  }, []);

  const heartbeat = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    ws.send(JSON.stringify({ t: "heartbeat" }));
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const token = localStorage.getItem("hu_session_token");
    if (!token) return undefined;

    let stopped   = false;
    let hbTimer   = null;
    let reconnectTimer = null;

    const connect = () => {
      if (stopped) return;
      let ws;
      try { ws = new WebSocket(wsUrl(token)); }
      catch { scheduleReconnect(); return; }
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        reconnectRef.current = 0;
        if (convIds && convIds.length) {
          ws.send(JSON.stringify({ t: "subscribe", conv_ids: convIds }));
        }
        hbTimer = setInterval(heartbeat, 25_000);
      };
      ws.onclose = () => {
        setConnected(false);
        clearInterval(hbTimer);
        if (!stopped) scheduleReconnect();
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
      ws.onmessage = (ev) => {
        let msg; try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.t === "ping") {
          ws.send(JSON.stringify({ t: "pong" }));
        } else if (msg.t === "new_msg" && onNewMessage) {
          onNewMessage(msg.d || {});
        } else if (msg.t === "presence" && onPresence) {
          onPresence(msg.d || {});
        } else if (msg.t === "typing" && onTyping) {
          onTyping(msg.d || {});
        }
      };
    };

    const scheduleReconnect = () => {
      reconnectRef.current = Math.min(reconnectRef.current + 1, 6);
      const delay = Math.min(30_000, 1000 * Math.pow(2, reconnectRef.current));
      reconnectTimer = setTimeout(connect, delay);
    };

    connect();

    return () => {
      stopped = true;
      clearInterval(hbTimer);
      clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch { /* ignore */ }
      wsRef.current = null;
    };
    // We intentionally omit onNewMessage/onPresence/onTyping handlers
    // from deps — they're captured by closure each render and we don't
    // want the WS to reconnect every render.
  }, [enabled, JSON.stringify(convIds)]);

  return { connected, sendTyping, heartbeat };
}
